import { useQuery } from '@tanstack/react-query';
import { api, ApiError, type FeedbackEntry } from '../api';
import { Alert, Badge, Card, EmptyState, PageHeader, Spinner } from '../components/ui';
import { IconStar } from '../components/icons';

/**
 * What people said when they pressed "Rate your experience". System
 * administrators only: it is about the system, and they are the ones who
 * can change it.
 */
export function AdminFeedback() {
  const feedback = useQuery({ queryKey: ['admin', 'feedback'], queryFn: () => api.adminFeedback() });

  if (feedback.isPending) return <Spinner label="Loading feedback" />;
  if (feedback.isError) {
    return <Alert>{feedback.error instanceof ApiError ? feedback.error.message : 'Could not load feedback.'}</Alert>;
  }

  const { summary, entries } = feedback.data;
  const most = Math.max(1, ...summary.distribution);

  return (
    <div>
      <PageHeader
        title="Feedback"
        description="What people said when they pressed Rate your experience, newest first. Only system administrators see this."
        actions={<Badge tone="neutral">{summary.count} response{summary.count === 1 ? '' : 's'}</Badge>}
      />

      {entries.length === 0 ? (
        <EmptyState
          icon={<IconStar />}
          title="Nothing yet"
          body="The pill in the bottom right of every screen sends ratings here. Nobody has pressed it so far."
        />
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <Tile label="Average rating" value={summary.average === null ? 'N/A' : summary.average.toFixed(1)} sub="out of 5, all time" />
            <Tile
              label="Last 30 days"
              value={summary.recent.average === null ? 'N/A' : summary.recent.average.toFixed(1)}
              sub={`from ${summary.recent.count} response${summary.recent.count === 1 ? '' : 's'}`}
            />
            <Card className="p-4">
              <div className="text-xs text-slate-600 dark:text-slate-400">How the ratings fall</div>
              <ul className="mt-2 space-y-1">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = summary.distribution[star - 1] ?? 0;
                  return (
                    <li key={star} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                      <span className="w-3 text-right tabular-nums">{star}</span>
                      <IconStar className="h-3 w-3 text-copper-500" filled />
                      <span className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <span
                          className="block h-full rounded-full bg-copper-500"
                          style={{ width: `${(count / most) * 100}%` }}
                        />
                      </span>
                      <span className="w-6 text-right tabular-nums">{count}</span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-3xl text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/60 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
                    <th className="px-5 py-3">When</th>
                    <th className="px-5 py-3">Who</th>
                    <th className="px-5 py-3">Rating</th>
                    <th className="px-5 py-3">Comment</th>
                    <th className="px-5 py-3">Where</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <Row key={entry.id} entry={entry} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Card className="p-4">
      <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{value}</div>
      <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">{label}</div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400">{sub}</div>
    </Card>
  );
}

function Row({ entry }: { entry: FeedbackEntry }) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0 dark:border-slate-800/70">
      <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
        {new Date(entry.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
      </td>
      <td className="px-5 py-3">
        {entry.user ? (
          <>
            <div className="text-slate-900 dark:text-slate-100">{entry.user.displayName}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{entry.user.email}</div>
          </>
        ) : (
          <span className="text-slate-500 dark:text-slate-400">A deleted account</span>
        )}
      </td>
      <td className="px-5 py-3">
        <span className="flex items-center gap-0.5" aria-label={`${entry.rating} out of 5`}>
          {[1, 2, 3, 4, 5].map((star) => (
            <IconStar
              key={star}
              className={`h-4 w-4 ${star <= entry.rating ? 'text-copper-500' : 'text-slate-300 dark:text-slate-700'}`}
              filled={star <= entry.rating}
            />
          ))}
        </span>
      </td>
      <td className="max-w-md px-5 py-3 text-slate-800 whitespace-pre-wrap dark:text-slate-200">
        {entry.comment || <span className="text-slate-500 dark:text-slate-400">No comment</span>}
      </td>
      <td className="px-5 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">{entry.path ?? ''}</td>
    </tr>
  );
}
